import { Delete } from 'lucide-react';
import { useState } from 'react';
import { Banner, BigButton, Screen, TextField } from '../components/ui';
import { useSession } from '../state/session';

/**
 * Влизане с ПИН. Никаква парола на телефон в кабината — човек с ръкавици,
 * на дъжда, в Пасау, не въвежда „Kmd!2026" (ADR 0005).
 */
export function PinScreen() {
  const { driver, unlockWithPin, busy, error, clearError } = useSession();
  const [pin, setPin] = useState('');

  async function submit(value: string) {
    const ok = await unlockWithPin(value);
    if (!ok) setPin('');
  }

  function press(digit: string) {
    clearError();
    const next = `${pin}${digit}`.slice(0, 4);
    setPin(next);
    if (next.length === 4) void submit(next);
  }

  return (
    <Screen logo title={driver ? `Здравей, ${driver.fullName.split(' ')[0] ?? ''}` : 'Вход'}>
      <p className="text-xl text-muted">Въведи своя четирицифрен ПИН.</p>

      <div className="my-8 flex justify-center gap-4">
        {[0, 1, 2, 3].map((index) => (
          <span
            key={index}
            className={`h-6 w-6 rounded-full border-4 ${
              index < pin.length ? 'border-ink bg-ink' : 'border-line'
            }`}
          />
        ))}
      </div>

      {error ? <Banner tone="stop">{error}</Banner> : null}

      <div className="mt-6 grid grid-cols-3 gap-3">
        {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((digit) => (
          <Key key={digit} onPress={() => press(digit)} disabled={busy}>
            {digit}
          </Key>
        ))}
        <span />
        <Key onPress={() => press('0')} disabled={busy}>
          0
        </Key>
        <Key
          onPress={() => {
            clearError();
            setPin((value) => value.slice(0, -1));
          }}
          disabled={busy}
        >
          <Delete size={32} />
        </Key>
      </div>
    </Screen>
  );
}

function Key({
  children,
  onPress,
  disabled,
}: {
  children: React.ReactNode;
  onPress: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onPress}
      disabled={disabled}
      className="flex items-center justify-center rounded-2xl border-2 border-line bg-card text-4xl font-bold active:bg-line disabled:opacity-40"
      style={{ minHeight: 84 }}
    >
      {children}
    </button>
  );
}

/**
 * Свързване на телефона. Става веднъж, в офиса: диспечерът диктува код от осем
 * знака, шофьорът си избира ПИН. Оттам нататък телефонът работи 180 дни.
 */
export function EnrolScreen() {
  const { enrol, busy, error, clearError } = useSession();
  const [code, setCode] = useState('');
  const [pin, setPin] = useState('');
  const [pinAgain, setPinAgain] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  const cleanCode = code.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  const ready = cleanCode.length === 8 && pin.length === 4 && pinAgain.length === 4;

  async function submit() {
    clearError();
    setLocalError(null);
    if (pin !== pinAgain) {
      setLocalError('Двата ПИН-а не съвпадат.');
      return;
    }
    if (/^(\d)\1{3}$/.test(pin) || pin === '1234') {
      setLocalError('Изберете друг ПИН — този се познава лесно.');
      return;
    }
    await enrol(cleanCode, pin);
  }

  return (
    <Screen logo title="Свързване на телефона">
      <div className="space-y-5">
        <p className="text-xl text-muted">
          Диспечерът ще ви даде код от осем знака. Въведете го веднъж — после ще влизате само с ПИН.
        </p>

        <TextField
          label="Код от диспечера"
          value={code}
          onChange={setCode}
          placeholder="A4K2-9QRT"
          maxLength={9}
          autoFocus
        />
        <TextField
          label="Измислете ПИН (4 цифри)"
          value={pin}
          onChange={(value) => setPin(value.replace(/\D/g, '').slice(0, 4))}
          inputMode="numeric"
          placeholder="••••"
        />
        <TextField
          label="Повторете ПИН-а"
          value={pinAgain}
          onChange={(value) => setPinAgain(value.replace(/\D/g, '').slice(0, 4))}
          inputMode="numeric"
          placeholder="••••"
        />

        {localError ?? error ? <Banner tone="stop">{localError ?? error}</Banner> : null}

        <BigButton tone="go" onClick={() => void submit()} disabled={!ready || busy}>
          {busy ? 'Свързва се…' : 'Свържи телефона'}
        </BigButton>

        <p className="text-base text-muted">
          ПИН-ът остава само на този телефон и не се изпраща никъде. Ако забравите ПИН-а или
          телефонът се загуби, диспечерът изключва устройството от офиса.
        </p>
      </div>
    </Screen>
  );
}

/** Крайният случай: приложението е отворено по незащитен адрес. */
export function UnsupportedScreen() {
  return (
    <Screen logo title="Отворете през защитен адрес">
      <Banner tone="warn">
        Приложението може да пази данни само когато е отворено през защитен адрес (https). Обадете
        се на диспечера — той ще ви даде правилния адрес.
      </Banner>
    </Screen>
  );
}
